<template>
  <div class="menu-page">
    <v-card class="nd-border" flat>
      <v-card-title>Kanban</v-card-title>

      <v-card-subtitle>
        Organize oportunidades com eficiência usando o Kanban. 
        <ib-btn small @click="showVideo = true">
          <v-icon size="16" class="mr-1">
            mdi-help-circle-outline
          </v-icon>
          Saiba mais
        </ib-btn>
      </v-card-subtitle>

      <v-card-text>
        <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 1rem;">
          <ib-btn color="primary" :small="$vuetify.breakpoint.sm || $vuetify.breakpoint.xs" @click="createFunnelDialog = true">
            <v-icon left>
              mdi-plus
            </v-icon>
            Novo funil
          </ib-btn>

          <div class="d-flex align-center" style="gap: 0.5rem;">
            <ib-select v-model="selectedFunnelId" :items="funnels" item-text="name" item-value="_id" label="Funil" outlined hide-details="auto" :style="$vuetify.breakpoint.smAndDown ? 'min-width: 150px' : 'max-width: 300px'" @change="loadFunnelData" />

            <ib-btn color="primary" :small="$vuetify.breakpoint.sm || $vuetify.breakpoint.xs" @click="filter_sheet = true">
              <v-icon :left="!$vuetify.breakpoint.xs">
                mdi-filter-outline
              </v-icon>
              <span v-if="!$vuetify.breakpoint.xs">Filtros</span>
            </ib-btn>

            <v-menu v-if="selectedFunnelId" offset-y left>
              <template #activator="{ on, attrs }">
                <v-btn class="primary" dark icon :small="$vuetify.breakpoint.sm || $vuetify.breakpoint.xs" v-bind="attrs" v-on="on">
                  <v-icon>mdi-cog-outline</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="toggleViewMode">
                  <v-list-item-icon class="mr-2">
                    <v-icon small color="primary">{{ viewMode === 'history' ? 'mdi-view-column-outline' : 'mdi-history' }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ viewMode === 'history' ? 'Voltar ao Kanban' : 'Ver Histórico' }}</v-list-item-title>
                </v-list-item>

                <v-divider />

                <v-list-item @click="openEditFunnelDialog">
                  <v-list-item-icon class="mr-2">
                    <v-icon small color="primary">mdi-pencil</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Editar nome do funil</v-list-item-title>
                </v-list-item>

                <v-divider />

                <v-list-item class="error--text" @click="deleteFunnelDialog = true">
                  <v-list-item-icon class="mr-2">
                    <v-icon small color="error">mdi-delete</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Apagar funil</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </v-card-text>

      <div v-if="filteredFunnel != null" class="mx-4 mb-2">
        <v-expansion-panels class="nd-border" flat>
          <v-expansion-panel>
            <v-expansion-panel-header style="font-weight: 500;">
              Existem filtros ativos
            </v-expansion-panel-header>
            <v-expansion-panel-content class="py-1">
              <div class="filter-bar">
                <div v-if="pre_filter.title" class="filter-item">
                  <v-icon small class="mr-1">
                    mdi-format-title
                  </v-icon>
                  <span>Título: {{ pre_filter.title }}</span>
                </div>
                <div v-if="pre_filter.contact && pre_filter.contact !== 'without_contact'" class="filter-item">
                  <v-icon small class="mr-1">
                    mdi-account
                  </v-icon>
                  <span>Contato: {{ pre_filter.contact.override_name || pre_filter.contact.name }}</span>
                </div>
                <div v-if="pre_filter.assigned_users?.length > 0 && !(pre_filter.assigned_users.length === 1 && pre_filter.assigned_users[0]._id === 'without_assigned_user')" class="filter-item">
                  <v-icon small class="mr-1">
                    mdi-account-tie
                  </v-icon>
                  <span>{{ getSelectedUsers }}</span>
                </div>
                <div v-if="pre_filter.date?.length > 0" class="filter-item">
                  <v-icon small class="mr-1">
                    mdi-calendar-range
                  </v-icon>
                  <span>Período: {{ getSelectedDateRange }}</span>
                </div>
                <div v-if="pre_filter.contact === 'without_contact'" class="filter-item">
                  <v-icon small class="mr-1">
                    mdi-account-off
                  </v-icon>
                  <span>Sem contato atribuído</span>
                </div>
                <div v-if="pre_filter.assigned_users?.length === 1 && pre_filter.assigned_users[0]._id === 'without_assigned_user'" class="filter-item">
                  <v-icon small class="mr-1">
                    mdi-account-cancel
                  </v-icon>
                  <span>Sem usuário responsável</span>
                </div>
                <div class="filter-item mb-1" style="display: flex; width: 100%;">
                  <v-spacer />
                  <ib-btn small dark outlined color="error" class="mr-4" @click="clearFilters">
                    <v-icon left>mdi-filter-remove-outline</v-icon>
                    Limpar filtros
                  </ib-btn>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

      <div v-if="displayedFunnel" ref="boardWrapper" class="kanban-board-wrapper ib-scroll">
        <draggable v-model="displayedFunnel.steps" :group="{ name: 'steps', pull: false, put: false }" class="steps-container" :animation="200" ghost-class="ghost-step" handle=".step-drag-handle" @change="handleStepMove">
          <div v-for="step in displayedFunnel.steps" :key="step._id" class="kanban-step" :style="{ borderTopColor: step.color || '#757575' }">
            <div class="step-header">
              <div class="d-flex align-center">
                <v-icon small class="step-drag-handle mr-1" style="cursor: grab;">
                  mdi-drag-vertical
                </v-icon>
                <v-icon small :color="step.color" class="mr-1">
                  mdi-circle
                </v-icon>
                <span class="step-title">{{ step.title }}</span>
                <v-chip x-small class="ml-2">
                  {{ getStepTotalCount(step) }}
                </v-chip>
              </div>

              <v-menu offset-y right>
                <template #activator="{ on, attrs }">
                  <ib-btn icon small v-bind="attrs" title="Opções da coluna" v-on="on">
                    <v-icon small>
                      mdi-dots-horizontal
                    </v-icon>
                  </ib-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="openCreateOpportunityDialog(step._id)">
                    <v-list-item-icon class="mr-2">
                      <v-icon small>
                        mdi-plus
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Nova oportunidade</v-list-item-title>
                  </v-list-item>

                  <v-list-item @click="editStepDialog = true; editingStep = true; selectedStep = step;">
                    <v-list-item-icon class="mr-2">
                      <v-icon small>
                        mdi-pencil
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Editar etapa</v-list-item-title>
                  </v-list-item>

                  <template v-if="getStepTotalCount(step) < 1">
                    <v-divider />
                    <v-list-item class="error--text" @click="deleteStep(step)">
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="error">
                          mdi-delete
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Excluir etapa</v-list-item-title>
                    </v-list-item>
                  </template>
                </v-list>
              </v-menu>
            </div>

            <draggable 
              v-model="step.opportunities" 
              :group="{ name: 'opportunities', pull: viewMode !== 'history', put: viewMode !== 'history' }" 
              :animation="200" 
              ghost-class="ghost-opportunity" 
              class="opportunities-container" 
              :data-step-id="step._id" 
              :disabled="viewMode === 'history'" 
              @change="handleOpportunityMove($event, step._id)"
            >
              <v-card
                v-for="opportunity in step.opportunities" :key="opportunity._id" class="kanban-opportunity mb-2" elevation="2" :style="{ borderLeftColor: step.color || '#0B9055' }" @click="viewOpportunity(opportunity)">
                <div class="opportunity-header">
                  <div class="d-flex align-center">
                    <ib-user-avatar :user="userById(opportunity.assigned_user)" size="32" class="mr-2" />
                    <span class="opportunity-title text-ellipsis">
                      {{ opportunity.title }}
                    </span>
                  </div>

                  <v-menu offset-y right>
                    <template #activator="{ on, attrs }">
                      <ib-btn icon small v-bind="attrs" class="opportunity-menu-btn" v-on="on" @click.stop>
                        <v-icon small>
                          mdi-dots-vertical
                        </v-icon>
                      </ib-btn>
                    </template>
                    <v-list dense>
                      <v-list-item @click="viewOpportunity(opportunity)">
                        <v-list-item-icon>
                          <v-icon small>
                            mdi-pencil
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Editar</v-list-item-title>
                      </v-list-item>

                      <v-list-item v-if="opportunity.contact" @click="goToContactConversations(opportunity.contact)">
                        <v-list-item-icon>
                          <v-icon small>
                            mdi-message-badge-outline
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Ver conversa(s)</v-list-item-title>
                      </v-list-item>

                      <v-list-item @click="openTransferOpportunityDialog(opportunity)">
                        <v-list-item-icon>
                          <v-icon small>
                            mdi-swap-vertical
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Transferir para outro funil</v-list-item-title>
                      </v-list-item>

                      <v-divider />

                      <v-list-item class="error--text" @click="deleteOpportunity(opportunity)">
                        <v-list-item-icon>
                          <v-icon small color="error">
                            mdi-delete
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Deletar</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>

                <v-card-text class="pa-0">
                  <div v-if="opportunity.description" class="text-ellipsis">
                    {{ opportunity.description }}
                  </div>

                  <div v-if="viewMode === 'history' && (opportunity.status === 'won' || opportunity.status === 'lost')" class="mt-2">
                    <ib-chip small :color="opportunity.status === 'won' ? '#0B9055' : '#DE3024'" class="mr-1" style="padding: 0 8px; height: 20px;">
                      {{ opportunity.status === 'won' ? 'Ganha' : 'Perdida' }}
                    </ib-chip>
                    <span v-if="opportunity.status === 'lost' && opportunity.loss_reason" class="text-caption">
                      Motivo: {{ opportunity.loss_reason }}
                    </span>
                  </div>

                  <div class="d-flex justify-space-between align-center mt-2 text-caption opportunity-footer">
                    <span v-if="opportunity.final_value && opportunity.status === 'won'" class="font-weight-medium opportunity-value" style="color: #0B9055">
                      {{ formatCurrency(opportunity.final_value) }}
                    </span>
                    <span v-else-if="opportunity.value" class="font-weight-medium opportunity-value" style="color: #0B9055">
                      {{ formatCurrency(opportunity.value) }}
                    </span>
                    <v-spacer />
                    <span>
                      <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                      {{ $date.smallFormat(opportunity.updated) }}
                    </span>
                  </div>
                </v-card-text>
              </v-card>
            </draggable>

            <div v-if="hasMoreOpportunities(step)" class="lazy-loading-indicator">
              <v-progress-circular indeterminate color="primary" size="20" width="2" class="mr-2" />
              <span class="text-caption grey--text text--darken-1">Carregando mais oportunidades...</span>
            </div>
            <div :ref="`lazySentinel-${step._id}`" class="lazy-sentinel" />

            <div v-if="!step.opportunities.length && !hasMoreOpportunities(step)" class="empty-step">
              <v-icon color="grey lighten-1">
                mdi-inbox
              </v-icon>
              <p class="grey--text text--lighten-1 mb-0 mt-2">
                Nenhuma Oportunidade
              </p>
            </div>
          </div>

          <div
            class="kanban-step add-step-card"
            @click="editStepDialog = true; editingStep = false;"
          >
            <div class="add-step-content">
              <v-icon large color="primary">
                mdi-plus-circle-outline
              </v-icon>
              <p class="primary--text font-weight-medium mt-2 mb-0">
                Adicionar Etapa
              </p>
            </div>
          </div>
        </draggable>
      </div>

      <div v-else-if="loading" class="kanban-board-wrapper ib-scroll">
        <div class="steps-container">
          <div v-for="n in 5" :key="n" class="kanban-step">
            <div class="step-header mb-3">
              <v-skeleton-loader type="chip" width="120" />
            </div>
            <div class="opportunities-container">
              <v-skeleton-loader v-for="i in 2" :key="i" type="card" class="mb-3" height="120" />
            </div>
          </div>
        </div>
      </div>

      <DialogCreateOpportunity 
        v-model="viewOpportunityDialog" 
        :opportunity="selectedOpportunity" 
        :funnels="[]" 
        :steps="currentFunnel ? currentFunnel.steps : []" 
        :users-list="users" 
        :contacts="contacts" 
        :loading-contacts="loadingContacts" 
        :saving="savingOpportunity" 
        :title="editingOpportunity ? 'Editar Oportunidade' : 'Nova Oportunidade'" 
        save-button-text="Salvar" 
        :show-contact-field="true" 
        :show-funnel-field="false" 
        :step-value="selectedOpportunityStepId" 
        :is-editing="editingOpportunity" 
        :status-loading="statusLoading" 
        @update-field="updateOpportunityField" 
        @contact-search="searchContacts" 
        @step-change="selectedOpportunityStepId = $event" 
        @save="saveOpportunity" 
        @mark-won="markOpportunityWon" 
        @mark-lost="markOpportunityLost" 
        @reopen="reopenOpportunity" 
      />

      <ib-dialog v-model="editStepDialog" max-width="400">
        <v-card>
          <ib-dialog-header :title="editingStep ? 'Editar Etapa' : 'Nova Etapa'" :close="() => { editStepDialog = false; editingStep = false }" />
          <v-card-text class="pb-0">
            <v-row>
              <v-col cols="12">
                <ib-text-field v-model="selectedStep.title" label="Título da etapa" outlined autofocus hide-details="auto" />
              </v-col>

              <v-col cols="12" class="d-flex align-center">
                <h3 class="mr-3">
                  Cor da etapa
                </h3>
                <input v-model="selectedStep.color" type="color" class="color-picker">
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions class="mt-4">
            <ib-btn color="gray200" outlined @click="editStepDialog = false; editingStep = false">
              <div class="gray1000--text">
                Cancelar
              </div>
            </ib-btn>
            <v-spacer />
            <ib-btn class="primary" :loading="savingStep" @click="saveStep">
              Salvar
            </ib-btn>
          </v-card-actions>
        </v-card>
      </ib-dialog>

      <ib-dialog v-model="createFunnelDialog" max-width="400">
        <v-card>
          <ib-dialog-header title="Criar Novo Funil" :close="() => createFunnelDialog = false" />
          <v-card-text class="pb-0">
            <v-row>
              <v-col cols="12">
                <ib-text-field v-model="funnelName" label="Nome do Funil" outlined autofocus hide-details="auto" placeholder="Vendas" />
              </v-col>
              <v-col cols="12">
                <p class="text-caption grey--text text--darken-1 mb-0">
                  O funil será criado com etapas padrão que você pode personalizar depois.
                </p>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions class="mt-4">
            <ib-btn color="gray200" outlined @click="createFunnelDialog = false">
              <div class="gray1000--text">
                Cancelar
              </div>
            </ib-btn>
            <v-spacer />
            <ib-btn class="primary" :loading="savingFunnel" @click="createOrUpdateFunnel('create')">
              Criar funil
            </ib-btn>
          </v-card-actions>
        </v-card>
      </ib-dialog>

      <ib-dialog v-model="editFunnelDialog" max-width="400">
        <v-card>
          <ib-dialog-header title="Editar Funil" :close="() => editFunnelDialog = false" />
          <v-card-text class="pb-0">
            <v-row>
              <v-col cols="12">
                <ib-text-field v-model="editingFunnelName" label="Nome do Funil" outlined autofocus hide-details="auto" />
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions class="mt-4">
            <ib-btn color="gray200" outlined @click="editFunnelDialog = false">
              <div class="gray1000--text">
                Cancelar
              </div>
            </ib-btn>
            <v-spacer />
            <ib-btn class="primary" :loading="savingFunnel" @click="createOrUpdateFunnel('edit')">
              Salvar
            </ib-btn>
          </v-card-actions>
        </v-card>
      </ib-dialog>

      <ib-dialog v-model="transferOpportunityDialog" max-width="500">
        <v-card>
          <ib-dialog-header title="Transferir Oportunidade" subtitle="Selecione o funil e a etapa de destino" :close="closeTransferOpportunityDialog" />
          <v-card-text class="pb-0">
            <v-row v-if="selectedOpportunity">
              <v-col cols="12">
                <div class="pa-2 rounded-lg gray75">
                  <div class="mb-1 gray600--text">
                    Oportunidade selecionada
                  </div>
                  <div class="font-weight-medium gray1000--text">
                    {{ selectedOpportunity.title }}
                  </div>
                </div>
              </v-col>

              <v-col cols="12">
                <ib-select v-model="transferTargetFunnelId" :items="availableFunnelsForTransfer" item-text="name" item-value="_id" label="Funil de destino" outlined hide-details="auto" @change="loadTransferFunnelSteps" />
              </v-col>

              <v-col v-if="transferTargetFunnelId && transferFunnelSteps.length > 0" cols="12">
                <ib-select v-model="transferTargetStepId" :items="transferFunnelSteps" item-text="title" item-value="_id" label="Etapa de destino" outlined hide-details="auto">
                  <template #selection="{ item }">
                    <v-icon small :color="item.color" class="mr-2">
                      mdi-circle
                    </v-icon>
                    {{ item.title }}
                  </template>
                  <template #item="{ item }">
                    <v-icon small :color="item.color" class="mr-2">
                      mdi-circle
                    </v-icon>
                    {{ item.title }}
                  </template>
                </ib-select>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions class="mt-4">
            <ib-btn color="gray200" outlined @click="closeTransferOpportunityDialog">
              <div class="gray1000--text">
                Cancelar
              </div>
            </ib-btn>
            <v-spacer />
            <ib-btn color="primary" :loading="transferringOpportunity" :disabled="!transferTargetFunnelId || !transferTargetStepId" @click="transferOpportunity">
              Transferir
            </ib-btn>
          </v-card-actions>
        </v-card>
      </ib-dialog>
    </v-card>

    <ib-dialog v-model="filter_sheet" max-width="600" persistent>
      <v-card>
        <ib-dialog-header title="Filtros" subtitle="Filtre as oportunidades do kanban" :close="() => { filter_sheet = false }" />
        
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <label class="filter-label">Busca por título da oportunidade</label>
              <ib-text-field v-model="pre_filter.title" prepend-inner-icon="mdi-format-title" outlined label="Título da oportunidade" hide-details="auto" />
            </v-col>

            <v-col cols="12">
              <label class="filter-label">Contato vinculado</label>
              <ib-autocomplete v-model="pre_filter.contact" return-object outlined :items="contactsWithSpecialOption" :loading="loadingContacts" clearable hide-details="auto" :item-text="getContactItemText" item-value="_id" label="Selecione um contato" placeholder="Busque por nome ou telefone" prepend-inner-icon="mdi-account-outline" no-filter @update:search-input="searchContacts">
                <template #no-data>
                  <v-list-item>
                    <v-list-item-title>
                      Busque um contato pelo
                      <strong>nome ou telefone</strong>
                    </v-list-item-title>
                  </v-list-item>
                </template>
                <template #selection="{ item }">
                  <div v-if="item._id === 'without_contact'" class="d-flex align-center">
                    <v-icon small class="mr-2">
                      mdi-account-off
                    </v-icon>
                    <span>{{ item.name }}</span>
                  </div>
                  <div v-else class="d-flex align-center">
                    <ib-user-avatar :user="item" size="24" class="mr-2" />
                    <span>{{ item.override_name || item.name }}</span>
                  </div>
                </template>
                <template #item="{ item }">
                  <div v-if="item._id === 'without_contact'" class="d-flex align-center">
                    <v-icon small class="mr-2">
                      mdi-account-off
                    </v-icon>
                    <v-list-item-content>
                      <v-list-item-title v-text="item.name" />
                    </v-list-item-content>
                  </div>
                  <div v-else class="d-flex align-center">
                    <ib-user-avatar :user="item" size="24" class="mr-2" />
                    <v-list-item-content>
                      <v-list-item-title v-text="item.override_name || item.name" />
                      <v-list-item-subtitle v-text="item.phone" />
                    </v-list-item-content>
                  </div>
                </template>
              </ib-autocomplete>
            </v-col>

            <v-col cols="12">
              <label class="filter-label">Responsáveis</label>
              <ib-autocomplete v-model="pre_filter.assigned_users" outlined :items="usersWithSpecialOption" :label="pre_filter.assigned_users?.length ? 'Responsáveis selecionados' : 'Todos os responsáveis'" prepend-inner-icon="mdi-account-tie" item-text="name" item-value="_id" return-object clearable multiple hide-details>
                <template #selection="{ item }">
                  <v-chip v-if="item._id === 'without_assigned_user'" class="ma-1">
                    <v-icon small class="mr-1">
                      mdi-account-cancel
                    </v-icon>
                    {{ item.name }}
                  </v-chip>
                  <v-chip v-else class="ma-1">
                    <ib-user-avatar :user="item" size="24" class="mr-1" />
                    {{ item.name }}
                  </v-chip>
                </template>
                <template #item="{ item }">
                  <div v-if="item._id === 'without_assigned_user'" class="d-flex align-center">
                    <v-icon small class="mr-2">
                      mdi-account-cancel
                    </v-icon>
                    <span>{{ item.name }}</span>
                  </div>
                  <div v-else class="d-flex align-center">
                    <ib-user-avatar :user="item" size="24" class="mr-2" />
                    <span>{{ item.name }}</span>
                  </div>
                </template>
              </ib-autocomplete>
            </v-col>

            <v-col cols="12">
              <label class="filter-label">Período de atualização</label>
              <v-menu ref="menuDate" v-model="menuDate" :close-on-content-click="false" :return-value.sync="pre_filter.date" transition="scale-transition" offset-y min-width="auto">
                <template #activator="{ on, attrs }">
                  <ib-text-field v-model="dateLabel" prepend-inner-icon="mdi-calendar-range" label="Selecione o período" clearable hide-details readonly v-bind="attrs" v-on="on" @click:clear="clearDate" />
                </template>
                <v-date-picker v-model="pre_filter.date" locale="pt-BR" color="primary" range no-title scrollable>
                  <v-spacer />
                  <ib-btn color="gray200" outlined @click="clearDate">
                    <div class="gray1000--text">
                      Limpar
                    </div>
                  </ib-btn>
                  <ib-btn color="primary" @click="applyDateFilter">
                    Aplicar
                  </ib-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="mt-4">
          <ib-btn color="gray200" outlined @click="clearFilters">
            <div class="gray1000--text">
              Limpar filtros
            </div>
          </ib-btn>
          <v-spacer />
          <ib-btn color="primary" @click="applyFilters">
            Aplicar filtros
          </ib-btn>
        </v-card-actions>
      </v-card>
    </ib-dialog>

    <ib-dialog v-model="deleteFunnelDialog" max-width="450">
      <v-card>
        <ib-dialog-header title="Apagar Funil" :close="() => deleteFunnelDialog = false" />
        <v-card-text class="pb-0">
          <p>Tem certeza que deseja apagar o funil <strong>{{ currentFunnelName }}</strong>?</p>
          <p class="text-caption grey--text">Todas as etapas e oportunidades deste funil serão excluídas permanentemente.</p>
        </v-card-text>

        <v-card-actions class="mt-4">
          <ib-btn color="gray200" outlined @click="deleteFunnelDialog = false">
            <div class="gray1000--text">Cancelar</div>
          </ib-btn>
          <v-spacer />
          <ib-btn color="error" :loading="savingFunnel" @click="createOrUpdateFunnel('delete')">
            Apagar funil
          </ib-btn>
        </v-card-actions>
      </v-card>
    </ib-dialog>

    <video-dialog v-model="showVideo" videoUrl="https://www.youtube.com/embed/-4BHB8OU-Fc?si=UgK06SVH8uVdS1Tw" />
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import Api from "@/lib/Api";
import { emitToastr, formatCurrency, formatMaskedCurrencyToNumber } from "@/lib/Utils";
import DialogCreateOpportunity from "@/components/DialogCreateOpportunity.vue";
import VideoDialog from "@/components/VideoDialog.vue";
let timeout = null;

export default {
  name: 'KanbanView',

  components: {
    draggable,
    DialogCreateOpportunity,
    VideoDialog
  },

  data() {
    return {
      loading: false,
      funnels: [],
      selectedFunnelId: null,
      currentFunnel: null,
      filteredFunnel: null,

      users: [],
      contacts: [],
      loadingContacts: false,

      createFunnelDialog: false,
      viewOpportunityDialog: false,
      editStepDialog: false,
      filter_sheet: false,
      transferOpportunityDialog: false,

      savingOpportunity: false,
      editingOpportunity: false,
      selectedOpportunity: null,
      selectedOpportunityStepId: null,

      savingStep: false,
      editingStep: null,
      selectedStep: { title: '', color: '#1976D2' },

      funnelName: '',

      editFunnelDialog: false,
      editingFunnelName: '',
      savingFunnel: false,

      deleteFunnelDialog: false,

      transferTargetFunnelId: null,
      transferTargetStepId: null,
      transferFunnelSteps: [],
      transferringOpportunity: false,

      pre_filter: {
        title: '',
        contact: null,
        assigned_users: [],
        date: []
      },

      menuDate: false,

      money: { decimal: ',', thousands: '.', prefix: 'R$ ', precision: 2, masked: false },

      lazyChunkSize: 20,
      stepObservers: {},
      activeFilters: null,

      showVideo: false,

      viewMode: 'kanban',
      statusLoading: false
    };
  },

  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },

    currentFunnelName() {
      const funnel = this.funnels.find(f => f._id === this.selectedFunnelId);
      return funnel?.name || '';
    },

    getSelectedUsers() {
      if (this.pre_filter?.assigned_users?.length > 0) {
        return 'Responsáveis: ' + this.pre_filter.assigned_users.map(u => u.name).join(', ');
      }
      return 'Todos os responsáveis';
    },

    displayedFunnel() {
      if (this.loading) return null;
      return this.filteredFunnel || this.currentFunnel;
    },

    availableFunnelsForTransfer() {
      return this.funnels.filter(f => f._id !== this.selectedFunnelId);
    },

    contactsWithSpecialOption() {
      return [
        { _id: 'without_contact', name: 'Sem contato atribuído', phone: '' },
        ...this.contacts
      ];
    },

    usersWithSpecialOption() {
      return [
        { _id: 'without_assigned_user', name: 'Sem usuário responsável', profile_picture: null },
        ...this.users
      ];
    },

    getSelectedDateRange() {
      const formatDate = (dateStr) => {
        if (!dateStr) return null;
        const date = new Date(dateStr + 'T00:00:00');
        return date.toLocaleDateString('pt-BR');
      };

      if (this.pre_filter.date && this.pre_filter.date.length === 2) {
        return `${formatDate(this.pre_filter.date[0])} até ${formatDate(this.pre_filter.date[1])}`;
      } else if (this.pre_filter.date && this.pre_filter.date.length === 1) {
        return `${formatDate(this.pre_filter.date[0])}`;
      }
      return 'Todas as datas';
    },

    dateLabel() {
      const fmt = s => s && s.length === 10 ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '';

      if (this.pre_filter.date && this.pre_filter.date.length) {
        return [fmt(this.pre_filter.date[0]), fmt(this.pre_filter.date[1])].filter(Boolean).join(' — ');
      }
      return '';
    }
  },

  watch: {
    viewOpportunityDialog(val) {
      if (val) return;
      this.selectedOpportunity = null;
      this.selectedOpportunityStepId = null;
      this.editingOpportunity = false;
    },

    'pre_filter.assigned_users'(val) {
      if (val?.length > 1 && val.some(u => u._id === 'without_assigned_user')) {
        this.pre_filter.assigned_users = [val.find(u => u._id === 'without_assigned_user')];
      }
    },

    viewMode() {
      this.loadFunnelData();
    }
  },

  mounted() {
    this.initializeBoard();
    this.getUsers();
  },

  beforeDestroy() {
    this.teardownStepObservers();
  },

  methods: {
    formatCurrency,
    formatMaskedCurrencyToNumber,

    toggleViewMode() {
      this.viewMode = this.viewMode === 'kanban' ? 'history' : 'kanban';
    },

    async initializeBoard() {
      const response = await Api.getFunnelsByAccount();
      if (response.error || !response.message) return;
      
      this.funnels = response.message;
      
      const lastSelectedFunnel = this.currentUser.config?.last_selected_funnel;
      const funnelStillAvailable = lastSelectedFunnel && this.funnels.some(f => f._id === lastSelectedFunnel);
      this.selectedFunnelId = funnelStillAvailable ? lastSelectedFunnel : this.funnels[0]?._id;
      
      await this.loadFunnelData();
    },

    async loadFunnelData() {
      if (!this.selectedFunnelId) return;

      const previousFunnelId = this.currentUser.config?.last_selected_funnel;
      const funnelChanged = previousFunnelId !== this.selectedFunnelId;

      this.loading = true;
      this.currentFunnel = null;
      this.filteredFunnel = null;
      this.activeFilters = null;
      this.pre_filter = { title: '', contact: null, assigned_users: [], date: [] };

      try {
        const statusFilter = this.viewMode === 'history' ? { status: ['won', 'lost'] } : {};
        const response = await Api.getFullFunnelById(this.selectedFunnelId, statusFilter);
        if (response.error) throw new Error(response.message);
        
        this.currentFunnel = this.decorateFunnel(this.sortOpportunities(response.message));
        
        if (funnelChanged) {
          await this.updateLastSelectedFunnel(this.selectedFunnelId);
        }
      } catch (error) {
        emitToastr('error', error.message || 'Erro ao carregar dados do funil');
      } finally {
        this.loading = false;
        this.scheduleLazyObserverSetup();
      }
    },

    async getUsers() {
      const resp = await Api.getTeamUsers();
      if (!resp.error && resp.message) this.users = resp.message;
    },

    async searchContacts(val) {
      clearTimeout(timeout);
      timeout = setTimeout(async () => {
        this.loadingContacts = true;
        const resp = await Api.getContactsByUser({ itemsPerPage: 5, query: val });
        
        if (!resp.error && resp.message) {
          this.contacts = resp.message.data;
          const selectedId = this.selectedOpportunity?.contact?._id;
          if (selectedId && !this.contacts.some(c => c._id === selectedId)) {
            this.contacts.unshift(this.selectedOpportunity.contact);
          }
        }
        this.loadingContacts = false;
      }, 250);
    },

    getContactItemText(item) {
      return item.override_name || item.name || item.phone || '';
    },

    updateOpportunityField({ field, value }) {
      if (this.selectedOpportunity) {
        this.selectedOpportunity[field] = value;
      }
    },

    async createOrUpdateFunnel(action = 'create') {
      try {
        let payload = {};
        let successMessage = '';

        if (action === 'create') {
          if (!this.funnelName?.trim()) return emitToastr('error', 'Por favor, insira um nome para o funil');

          payload = { name: this.funnelName };
          successMessage = 'Funil criado com sucesso!';
        } else if (action === 'edit') {
          if (!this.editingFunnelName?.trim()) return emitToastr('error', 'Por favor, insira um nome para o funil');

          payload = { _id: this.selectedFunnelId, name: this.editingFunnelName };
          successMessage = 'Nome do funil atualizado com sucesso!';
        } else if (action === 'delete') {
          payload = { _id: this.selectedFunnelId, name: this.currentFunnelName, filed: true };
          successMessage = 'Funil apagado com sucesso';
        }

        this.savingFunnel = true;
        const response = await Api.createOrUpdateFunnel(payload);

        if (response?.error) return emitToastr('error', response.message);

        if (action === 'create') {
          this.funnels.push(response.message);
          this.selectedFunnelId = response.message._id;
          await this.loadFunnelData();
          this.createFunnelDialog = false;
          this.funnelName = '';

        } else if (action === 'edit') {
          const funnelIndex = this.funnels.findIndex(f => f._id === this.selectedFunnelId);
          if (funnelIndex > -1) this.funnels[funnelIndex].name = this.editingFunnelName;
          this.editFunnelDialog = false;
          this.editingFunnelName = '';
          
        } else if (action === 'delete') {
          const index = this.funnels.findIndex(f => f._id === this.selectedFunnelId);
          if (index > -1) this.funnels.splice(index, 1);

          this.selectedFunnelId = this.funnels[0]?._id || null;

          if (this.selectedFunnelId) {
            await this.loadFunnelData();
          } else {
            this.currentFunnel = null;
            this.filteredFunnel = null;
          }
          this.deleteFunnelDialog = false;
        }

        emitToastr('success', successMessage);
      } catch (error) {
        emitToastr('error', 'Erro ao processar funil');
      } finally {
        this.savingFunnel = false;
      }
    },

    openEditFunnelDialog() {
      const currentFunnel = this.funnels.find(f => f._id === this.selectedFunnelId);
      if (!currentFunnel) return;

      this.editingFunnelName = currentFunnel.name;
      this.editFunnelDialog = true;
    },

    async handleStepMove(event) {
      if (!event.moved) return;
      this.currentFunnel.steps.forEach((step, index) => step.order = index);
      this.updateStepOrders();
    },

    async updateStepOrders() {
      const updatePromises = this.currentFunnel.steps.map((step, index) => {
        return Api.createOrUpdateStep({
          _id: step._id,
          title: step.title,
          color: step.color,
          funnel_id: this.selectedFunnelId,
          order: index
        });
      });

      await Promise.all(updatePromises);
    },

    async handleOpportunityMove(event, stepId) {
      const step = this.currentFunnel.steps.find(s => s._id === stepId);
      if (!step) return;

      if (event.removed) {
        step.totalOpportunities = Math.max(step.totalOpportunities - 1, step.opportunities.length);
        this.maybeTopUpStep(step);
        return;
      }

      const change = event.added || event.moved;
      if (!change) return;

      const opportunity = change.element;
      const newIndex = change.newIndex;
      const beforeOpportunity = step.opportunities[newIndex - 1];
      const afterOpportunity = step.opportunities[newIndex + 1];

      try {
        const response = await Api.updateOpportunityPosition(
          opportunity._id,
          stepId,
          beforeOpportunity?._id || null,
          afterOpportunity?._id || null
        );

        if (response?.error) throw new Error(response.message);

        if (response.message) {
          this.$set(step.opportunities, newIndex, { ...opportunity, ...response.message });
        }

        if (event.added) {
          step.totalOpportunities = Math.max(step.totalOpportunities + 1, step.opportunities.length);
        }
      } catch (error) {
        emitToastr('error', 'Erro ao atualizar posição da oportunidade');
        await this.loadFunnelData();
      }
    },

    openCreateOpportunityDialog(stepId) {
      this.selectedOpportunity = { title: '', description: '', value: '', contact: null, assigned_user: null };
      this.selectedOpportunityStepId = stepId;
      this.editingOpportunity = false;
      this.viewOpportunityDialog = true;
    },

    async viewOpportunity(opportunity) {
      const step = this.findStepByOpportunityId(opportunity._id);

      this.selectedOpportunity = { ...opportunity, value: formatCurrency(opportunity.value) };

      if (this.selectedOpportunity.contact) {
        const resp = await Api.getContactById(this.selectedOpportunity.contact);
        if (resp && !resp.error) {
          this.selectedOpportunity.contact = resp.message;

          const contactExists = this.contacts.some(c => c._id === resp.message._id);
          if (!contactExists) this.contacts.unshift(resp.message);
        }
      }
      this.selectedOpportunityStepId = step ? step._id : null;
      this.editingOpportunity = true;
      this.viewOpportunityDialog = true;
    },

    async saveOpportunity() {
      this.savingOpportunity = true;

      try {
        const opportunityData = {
          ...this.selectedOpportunity,
          value: formatMaskedCurrencyToNumber(this.selectedOpportunity.value),
          contact: this.selectedOpportunity.contact?._id || null,
          assigned_user: this.selectedOpportunity.assigned_user || null,
          funnel: this.selectedFunnelId,
          step: this.selectedOpportunityStepId
        };

        const response = await Api.createOrUpdateOpportunity(opportunityData);
        if (response?.error) return emitToastr('error', response.message);

        const savedOpportunity = {
          ...opportunityData,
          ...response.message,
        };

        if (this.editingOpportunity) {
          const oldStep = this.findStepByOpportunityId(this.selectedOpportunity._id);
          const newStepId = this.selectedOpportunityStepId;
          const changedStep = oldStep?._id !== newStepId;

          if (changedStep) {
            if (oldStep) this.removeOpportunityFromStep(oldStep, this.selectedOpportunity._id);

            const newStep = this.currentFunnel.steps.find(s => s._id === newStepId);
            if (newStep) {
              const lastOpportunity = newStep.opportunities[newStep.opportunities.length - 1];
              const resp = await Api.updateOpportunityPosition(
                savedOpportunity._id,
                newStepId,
                lastOpportunity?._id || null,
                null
              );

              this.insertOpportunityIntoStep(newStep, {...savedOpportunity, ...resp.message, step: newStepId}, true);
            }
          } else if (oldStep) {
            this.updateOpportunityInStep(oldStep, { ...savedOpportunity, step: oldStep._id });
          }

          emitToastr('success', 'Oportunidade atualizada com sucesso');
        } else {
          const step = this.currentFunnel.steps.find(s => s._id === this.selectedOpportunityStepId);
          if (step) this.insertOpportunityIntoStep(step, savedOpportunity, true);
          
          emitToastr('success', 'Oportunidade criada com sucesso');
        }

        this.viewOpportunityDialog = false;
      } catch (error) {
        emitToastr('error', 'Erro ao salvar oportunidade');
      } finally {
        this.savingOpportunity = false;
        this.scheduleLazyObserverSetup();
      }
    },

    async deleteOpportunity(opportunity) {
      const step = this.findStepByOpportunityId(opportunity._id);
      if (!step) return;

      const removalContext = this.removeOpportunityFromStep(step, opportunity._id, { skipTopUp: true });
      if (!removalContext) return;

      try {
        await Api.createOrUpdateOpportunity({ ...opportunity, filed: true });
        emitToastr('success', 'Oportunidade excluída com sucesso');
        this.maybeTopUpStep(step);
      } catch (error) {
        step.opportunities.splice(removalContext.removedIndex, 0, removalContext.removedOpportunity);
        step.totalOpportunities++;
        emitToastr('error', 'Erro ao excluir oportunidade');
      } finally {
        this.scheduleLazyObserverSetup();
      }
    },

    async saveStep() {
      this.savingStep = true;

      if (this.editingStep) {
        const response = await Api.createOrUpdateStep(this.selectedStep);
        if (response && response.error) return emitToastr('error', response.message);
        emitToastr('success', 'Etapa atualizada com sucesso');
      } else {
        const newStep = {
          ...this.selectedStep,
          funnel: this.selectedFunnelId,
          order: this.currentFunnel.steps.length
        };

        const response = await Api.createOrUpdateStep(newStep);
        if (response && response.error) return emitToastr('error', response.message);

        response.message.opportunities = [];
        response.message.loadedAmount = 0;
        response.message.totalOpportunities = 0;
        response.message.isLoadingMore = false;
        this.currentFunnel.steps.push(response.message);
        this.decorateFunnel(this.currentFunnel);
        this.scheduleLazyObserverSetup();

        emitToastr('success', 'Etapa criada com sucesso');
      }
      this.editStepDialog = false;
      this.editingStep = false;
      this.selectedStep = { title: '', color: '#1976D2' };
      this.savingStep = false;
    },

    async deleteStep(step) {
      step.filed = true;
      const resp = await Api.createOrUpdateStep(step);
      if (resp && resp.error) return emitToastr('error', resp.message);
      emitToastr('success', 'Etapa excluída com sucesso');
      this.loadFunnelData();
    },

    async updateLastSelectedFunnel(funnel_id) {
      let user_config = this.currentUser.config || { last_selected_funnel: '' };
      user_config.last_selected_funnel = funnel_id;
      await Api.updateUserConfig(user_config);
    },

    userById(id) {
      if (!id) return { name: 'Sem responsável', profile_picture: null };
      return this.users.find(u => u._id === id) || { name: 'Usuário', profile_picture: null };
    },

    clearFilters() {
      this.pre_filter = { title: '', contact: null, assigned_users: [], date: [] };
      this.filteredFunnel = null;
      this.activeFilters = null;
      this.filter_sheet = false;
      this.scheduleLazyObserverSetup();
    },

    applyDateFilter() {
      this.$refs.menuDate.save(this.pre_filter.date);
      this.menuDate = false;
    },

    clearDate() {
      this.pre_filter.date = [];
      this.$refs.menuDate.save(this.pre_filter.date);
      this.menuDate = false;
    },

    hasActiveFilters() {
      return this.pre_filter.title || this.pre_filter.contact || this.pre_filter.assigned_users?.length > 0 || this.pre_filter.date?.length > 0;
    },

    buildFilterParams() {
      const params = {};
      const { title, contact, assigned_users, date } = this.pre_filter;

      if (this.viewMode === 'history') {
        params.status = ['won', 'lost'];
      }

      if (title) params.title = title;

      if (contact) {
        contact._id === 'without_contact' ? params.without_contact = true : params.contact_id = contact._id;
      }

      if (assigned_users?.length) {
        assigned_users[0]._id === 'without_assigned_user' ? params.without_assigned_user = true : params.assigned_user_ids = assigned_users.map(u => u._id);
      }

      if (date?.length > 0) {
        params.date_start = date[0];
        if (date.length === 2) {
          params.date_end = date[1];
        } else {
          params.date_end = date[0];
        }
      }

      return params;
    },

    sortOpportunitiesByOrder(opportunities) {
      return (opportunities || []).sort((a, b) => (b.order || 0) - (a.order || 0));
    },

    sortOpportunities(funnelData) {
      funnelData.steps.forEach(step => { step.opportunities = this.sortOpportunitiesByOrder(step.opportunities) });
      return funnelData;
    },

    async applyFilters() {
      if (!this.currentFunnel) return;

      if (!this.hasActiveFilters()) {
        this.filteredFunnel = null;
        this.activeFilters = null;
        this.filter_sheet = false;
        this.scheduleLazyObserverSetup();
        return;
      }

      this.filteredFunnel = null;
      this.loading = true;
      this.filter_sheet = false;

      try {
        const filters = this.buildFilterParams();
        const response = await Api.getFullFunnelById(this.selectedFunnelId, filters);
        this.activeFilters = filters;
        this.filteredFunnel = this.decorateFunnel(this.sortOpportunities(response.message));
      } catch (error) {
        emitToastr('error', 'Erro ao aplicar filtros');
      } finally {
        this.loading = false;
        this.scheduleLazyObserverSetup();
      }
    },

    openTransferOpportunityDialog(opportunity) {
      this.selectedOpportunity = { ...opportunity };
      this.transferTargetFunnelId = null;
      this.transferTargetStepId = null;
      this.transferFunnelSteps = [];
      this.transferOpportunityDialog = true;
    },

    closeTransferOpportunityDialog() {
      this.transferOpportunityDialog = false;
      this.selectedOpportunity = null;
      this.transferTargetFunnelId = null;
      this.transferTargetStepId = null;
      this.transferFunnelSteps = [];
    },

    async loadTransferFunnelSteps() {
      if (!this.transferTargetFunnelId) {
        this.transferFunnelSteps = [];
        this.transferTargetStepId = null;
        return;
      }

      const response = await Api.getStepsByFunnel(this.transferTargetFunnelId);
      if (response && !response.error) {
        this.transferFunnelSteps = response.message || [];
        this.transferTargetStepId = this.transferFunnelSteps[0]?._id || null;
      }
    },

    async transferOpportunity() {
      if (!this.selectedOpportunity || !this.transferTargetFunnelId || !this.transferTargetStepId) {
        return emitToastr('error', 'Selecione um funil e uma etapa de destino');
      }
      const opportunityData = {
        ...this.selectedOpportunity,
        value: typeof this.selectedOpportunity.value === 'number' ? this.selectedOpportunity.value : formatMaskedCurrencyToNumber(this.selectedOpportunity.value),
        funnel: this.transferTargetFunnelId,
        step: this.transferTargetStepId
      };

      this.transferringOpportunity = true;
      const response = await Api.createOrUpdateOpportunity(opportunityData);
      this.transferringOpportunity = false;

      if (response && response.error) {
        return emitToastr('error', response.message);
      }

      await Api.updateOpportunityPosition(this.selectedOpportunity._id, this.transferTargetStepId, null, null);

      const currentStep = this.findStepByOpportunityId(this.selectedOpportunity._id);
      if (currentStep) {
        this.removeOpportunityFromStep(currentStep, this.selectedOpportunity._id);
      }

      emitToastr('success', 'Oportunidade transferida com sucesso!');
      this.closeTransferOpportunityDialog();
      this.scheduleLazyObserverSetup();
    },

    async goToContactConversations(contactId) {
      const resp = await Api.getContactById(contactId);

      if (resp && !resp.error && resp.message) {
        const contact = resp.message;

        this.$router.push({ name: 'Atendimento', params: { contactFilter: contact } });
      } else {
        emitToastr('error', 'Não foi possível carregar o contato');
      }
    },

    getStepTotalCount(step) {
      return step?.totalOpportunities || step?.opportunities?.length || 0;
    },

    hasMoreOpportunities(step) {
      if (!step) return false;
      const loaded = step.opportunities?.length || 0;
      const total = step.totalOpportunities || loaded;
      return loaded < total;
    },

    decorateFunnel(funnel) {
      if (!funnel?.steps) return funnel;
      funnel.steps.forEach(step => {
        step.opportunities = step.opportunities || [];
        step.totalOpportunities = step.totalOpportunities || step.opportunities.length;
        step.isLoadingMore = false;
      });
      return funnel;
    },

    findStepByOpportunityId(opportunityId) {
      if (!opportunityId) return null;
      const searchInFunnel = funnel => funnel?.steps?.find(step => step.opportunities?.some(o => o._id === opportunityId));
      return searchInFunnel(this.filteredFunnel) || searchInFunnel(this.currentFunnel) || null;
    },

    updateOpportunityInStep(step, updatedOpportunity) {
      if (!step || !updatedOpportunity?._id) return;
      const idx = step.opportunities.findIndex(o => o._id === updatedOpportunity._id);
      if (idx > -1) this.$set(step.opportunities, idx, { ...step.opportunities[idx], ...updatedOpportunity });
    },

    removeOpportunityFromStep(step, opportunityId, { skipTopUp = false } = {}) {
      if (!step || !opportunityId) return null;
      const index = step.opportunities.findIndex(o => o._id === opportunityId);
      if (index === -1) return null;

      const [removedOpportunity] = step.opportunities.splice(index, 1);
      step.totalOpportunities = Math.max((step.totalOpportunities || 0) - 1, step.opportunities.length);

      if (!skipTopUp) this.maybeTopUpStep(step);

      return { removedOpportunity, removedIndex: index };
    },

    async maybeTopUpStep(step) {
      if (!step || step.isLoadingMore || !this.hasMoreOpportunities(step)) return;

      const visible = step.opportunities.length;
      const desiredVisible = Math.min(this.lazyChunkSize, step.totalOpportunities);

      if (visible < desiredVisible) {
        await this.loadMoreOpportunities(step._id, desiredVisible - visible);
      }
    },

    insertOpportunityIntoStep(step, opportunityData, isNew = false) {
      if (!step || !opportunityData) return;

      const existingIndex = step.opportunities.findIndex(o => o._id === opportunityData._id);

      if (existingIndex > -1) {
        this.$set(step.opportunities, existingIndex, { ...step.opportunities[existingIndex], ...opportunityData });
      } else {
        step.opportunities.push(opportunityData);
        if (isNew) step.totalOpportunities++;
      }

      step.opportunities.sort((a, b) => (b.order || 0) - (a.order || 0));
    },

    scheduleLazyObserverSetup() {
      this.$nextTick(() => this.resetLazyObservers());
    },

    resetLazyObservers() {
      this.teardownStepObservers();
      this.$nextTick(() => this.displayedFunnel?.steps?.forEach(step => this.setupStepObserver(step)));
    },

    setupStepObserver(step) {
      if (!step || !this.hasMoreOpportunities(step)) return;

      const sentinelRef = this.$refs[`lazySentinel-${step._id}`];
      const sentinel = Array.isArray(sentinelRef) ? sentinelRef[0] : sentinelRef;
      if (!sentinel) return;

      this.disconnectStepObserver(step._id);

      const observer = new IntersectionObserver(
        entries => { if (entries[0].isIntersecting) this.loadMoreOpportunities(step._id); },
        { root: this.$refs.boardWrapper, rootMargin: '200px' }
      );

      observer.observe(sentinel);
      this.stepObservers[step._id] = observer;
    },

    disconnectStepObserver(stepId) {
      if (!stepId || !this.stepObservers[stepId]) return;
      this.stepObservers[stepId].disconnect();
      this.$delete(this.stepObservers, stepId);
    },

    teardownStepObservers() {
      Object.values(this.stepObservers).forEach(observer => observer?.disconnect());
      this.stepObservers = {};
    },

    async loadMoreOpportunities(stepId, customLimit = null) {
      const step = this.displayedFunnel?.steps?.find(s => s._id === stepId);
      if (!step || step.isLoadingMore || !this.hasMoreOpportunities(step)) return;

      step.isLoadingMore = true;

      try {
        const skip = step.opportunities.length;
        const limit = customLimit || this.lazyChunkSize;

        const lazyFilters = { ...this.activeFilters };
        if (this.viewMode === 'history' && !lazyFilters.status) lazyFilters.status = ['won', 'lost'];

        const response = await Api.getFunnelStepOpportunities(this.selectedFunnelId, stepId, skip, limit, lazyFilters);
        if (response?.error) throw new Error(response.message);

        const { opportunities = [], total } = response.message || {};
        const existingIds = new Set(step.opportunities.map(o => o._id));
        const newOpportunities = opportunities.filter(o => !existingIds.has(o._id));

        step.opportunities.push(...newOpportunities);
        step.opportunities.sort((a, b) => (b.order || 0) - (a.order || 0));
        step.totalOpportunities = total || step.totalOpportunities;

        if (!this.hasMoreOpportunities(step)) {
          this.disconnectStepObserver(stepId);
        }
      } catch (error) {
        emitToastr('error', 'Erro ao carregar mais oportunidades');
      } finally {
        step.isLoadingMore = false;
      }
    },

    async markOpportunityWon({ finalValue }) {
      if (!this.selectedOpportunity?._id) return;

      this.statusLoading = true;
      try {
        const response = await Api.updateOpportunityStatus(this.selectedOpportunity._id, 'won', finalValue);
        if (response?.error) return emitToastr('error', response.message);

        const step = this.findStepByOpportunityId(this.selectedOpportunity._id);
        if (step) this.removeOpportunityFromStep(step, this.selectedOpportunity._id);

        this.viewOpportunityDialog = false;
        emitToastr('success', 'Oportunidade marcada como ganha!');
      } catch (error) {
        emitToastr('error', 'Erro ao marcar oportunidade como ganha');
      } finally {
        this.statusLoading = false;
      }
    },

    async markOpportunityLost({ lossReason }) {
      if (!this.selectedOpportunity?._id) return;

      this.statusLoading = true;
      try {
        const response = await Api.updateOpportunityStatus(this.selectedOpportunity._id, 'lost', null, lossReason);
        if (response?.error) return emitToastr('error', response.message);

        const step = this.findStepByOpportunityId(this.selectedOpportunity._id);
        if (step) this.removeOpportunityFromStep(step, this.selectedOpportunity._id);

        this.viewOpportunityDialog = false;
        emitToastr('success', 'Oportunidade marcada como perdida');
      } catch (error) {
        emitToastr('error', 'Erro ao marcar oportunidade como perdida');
      } finally {
        this.statusLoading = false;
      }
    },

    async reopenOpportunity() {
      if (!this.selectedOpportunity?._id) return;

      this.statusLoading = true;
      try {
        const response = await Api.updateOpportunityStatus(this.selectedOpportunity._id, 'open');
        if (response?.error) return emitToastr('error', response.message);

        if (this.viewMode === 'history') {
          const step = this.findStepByOpportunityId(this.selectedOpportunity._id);
          if (step) this.removeOpportunityFromStep(step, this.selectedOpportunity._id);
        }

        this.viewOpportunityDialog = false;
        emitToastr('success', 'Oportunidade reaberta com sucesso!');
      } catch (error) {
        emitToastr('error', 'Erro ao reabrir oportunidade');
      } finally {
        this.statusLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.menu-page {
  height: calc(100vh - 140px);
  max-width: 100%;
}

.filter-bar {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-item {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: var(--v-gray600-base);
}

.filter-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--v-gray800-base);
  margin-bottom: 8px;
}

.kanban-board-wrapper {
  height: 72vh;
  overflow-x: auto;
  overflow-y: auto;
  padding: 1rem;
}

.steps-container {
  display: flex;
  gap: 1rem;
  min-width: min-content;
  animation: fadeIn 0.3s ease-in-out;
}

.kanban-step {
  flex: 0 0 300px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background-color: var(--v-gray50-base);
  border-radius: 8px;
  border-top: 4px solid var(--v-gray400-base);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.step-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 8px;
  background-color: var(--v-gray25-base);
  border-bottom: 1px solid var(--v-gray200-base);
}

.step-title {
  font-weight: 600;
  font-size: 14px;
  color: var(--v-gray950-base);
}

.opportunities-container {
  flex: 1;
  padding: 8px;
  min-height: 70vh;
  background-color: var(--v-gray100-base);
}

.kanban-opportunity {
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 3px solid;
  padding: 8px;
  gap: 12px;
}

.kanban-opportunity:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
  transform: translateY(-2px);
}

.opportunity-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.opportunity-header > .d-flex {
  flex: 1;
  min-width: 0;
}

.opportunity-title {
  font-size: 14px;
  color: var(--v-gray950-base);
  font-weight: 600;
  min-width: 0;
}

.ghost-opportunity {
  opacity: 0.5;
  background: #e3f2fd;
  border: 2px dashed #0B9055;
}

.lazy-loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  color: var(--v-gray600-base);
}

.lazy-sentinel {
  width: 100%;
  height: 1px;
  background-color: var(--v-gray100-base);
}

.empty-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  background-color: var(--v-gray100-base);
}

.add-step-card {
  background-color: transparent !important;
  border: 2px dashed var(--v-primary-base) !important;
  box-shadow: none !important;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 200px;
}

.add-step-card:hover {
  background-color: rgba(25, 118, 210, 0.05) !important;
  border-color: var(--v-primary-base) !important;
  transform: scale(1.02);
}

.add-step-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.kanban-opportunity {
  animation: slideIn 0.3s ease;
}

.step-drag-handle {
  cursor: grab !important;
  opacity: 0.5;
  transition: opacity 0.2s;
}

.step-drag-handle:hover {
  opacity: 1;
}

.step-drag-handle:active {
  cursor: grabbing !important;
}

.ghost-step {
  opacity: 0.4;
  background: #e8f5e9;
  border: 2px dashed var(--v-primary-base);
}

.text-ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

@media (max-width: 600px) {
  .kanban-step {
    flex: 0 0 260px !important;
  }
  
  .step-header {
    padding: 8px 6px !important;
  }
  
  .step-title {
    font-size: 13px !important;
  }
  
  .opportunities-container {
    padding: 8px !important;
  }
  
  .opportunity-title {
    font-size: 13px !important;
  }

  .opportunity-footer {
    font-size: 11px !important;
  }
}
</style>